<template>
  <main class="view users">
    <div class="descriptor">
      <h1>{{ $t('users.headline') }}</h1>
      <p>{{ $t('users.copy') }}</p>
      <Button primary @click="createUser">{{ $t('users.buttons.newUser') }}</Button>
    </div>
    <List
      :filters="computedListFilters"
      :structure="computedStructure"
      :data="computedListData"
      :sort.sync="sort"
      @interaction="handleListInteraction"
      @select="handleItemSelect"
      @context="handleListContext"
    />
  </main>
</template>

<script>
  import StatusUser from '@/components/ui/StatusUser';
  import List from '@/components/ui/list/List';
  import ToggleBar from '@/components/ui/inputs/ToggleBar';
  import Dropdown from '@/components/ui/inputs/Dropdown';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Button from '@/components/ui/inputs/Button';
  import { Store } from '@/store';
  import Format from '@/utils/Format';
  import VueUtil from '@/utils/VueUtil';
  import ModalUserCreateEdit from '@/components/modals/ModalUserCreateEdit';
  import ContextMenu from '@/components/ui/inputs/ContextMenu';
  import ModalUserLock from '@/components/modals/ModalUserLock';
  import ModalUserDelete from '@/components/modals/ModalUserDelete';
  import Toast from '@/components/ui/Toast';
  import Sort from '@/utils/Sort';

  export default {
    name: 'Users',
    components: {
      Button,
      ToggleBar,
      List,
      StatusUser,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {},
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        users: null,
        filters: {},
        sort: {
          property: 'created',
          ascending: false,
        },
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedStructure() {
        const columnLabels = this.$i18n.t('users.list.labels');
        const isVSAManager = Store.permissions.branchCanCreate;
        return {
          firstname: {
            label: columnLabels.firstname,
          },
          lastname: {
            label: columnLabels.lastname,
          },
          ...(isVSAManager ? {
            branch: {
              label: columnLabels.branch,
              format: v => v?.name || '-',
              sort: Sort.list.branch,
            },
          } : {}),
          role: {
            label: columnLabels.role,
            format: Format.toI18nRole,
          },
          ...(!isVSAManager ? {
            inquiries: {
              label: columnLabels.inquiries,
              sort: Sort.list.number,
            },
          } : {}),
          status: {
            label: columnLabels.status,
            component: StatusUser,
          },
        };
      },
      computedListFilters() {
        const isVSAManager = Store.permissions.branchCanCreate;
        const globalLabels = this.$i18n.t('labels');
        const filterLabels = this.$i18n.t('users.list.filters');
        return [
          {
            id: 'status',
            type: ToggleBar,
            props: {
              secondary: true,
              value: 'all',
              options: [
                { value: 'all', label: filterLabels.statuses.all },
                { value: 'active', label: filterLabels.statuses.active },
                { value: 'locked', label: filterLabels.statuses.locked },
              ],
            },
          },
          {
            id: 'role',
            type: Dropdown,
            props: {
              placeholderPrefix: filterLabels.role,
              placeholder: globalLabels.all,
              multiselect: true,
              tertiary: true,
              options: this.computedRoles,
            },
          },
          (isVSAManager ? {
            id: 'branch',
            type: Dropdown,
            props: {
              placeholderPrefix: filterLabels.branch,
              placeholder: globalLabels.all,
              multiselect: true,
              tertiary: true,
              options: this.computedBranches,
            },
          } : {}),
          {
            id: 'search',
            type: TextInput,
            props: {
              id: 'list-search-input',
              icon: 'search',
              placeholder: globalLabels.search,
            },
          },
        ];
      },
      computedListActions() {
        return [
          { action: 'edit', icon: 'edit', label: 'Bearbeiten' },
          { action: 'sendpw', icon: 'passwordShow', label: 'Passwort senden' },
          { action: 'lockToggle', icon: 'archive', label: 'Sperren' },
          { action: 'delete', icon: 'delete', label: 'Löschen' },
        ];
      },
      computedRoles() {
        const statuses = this.$i18n.t('roles');
        const keys = Object.keys(statuses);
        return keys.map(key => ({
          value: key, label: statuses[key],
        }));
      },
      computedBranches() {
        let out = [];
        const users = this.users;
        if (users) {
          users.forEach((u) => {
            if (u.branch && !out.find(i => i.value === u.branch.uuid)) {
              out.push({ value: u.branch.uuid, label: u.branch.name });
            }
          });
          out = out.sort((a, b) => a.label.localeCompare(b.label));
        }
        return out;
      },
      computedListData() {
        const filters = this.filters;
        const users = this.users;
        if (users) {
          let out = (users || []).slice();
          if (filters.status && filters.status !== 'all') {
            out = out.filter(e => e.locked === (filters.status !== 'active'));
          }
          if (filters.role?.length > 0) {
            out = out.filter(e => filters.role.includes(e.role));
          }
          if (filters.branch?.length > 0) {
            out = out.filter(e => filters.branch.includes(e.branch?.uuid));
          }
          if (filters.search) {
            const search = filters.search.toLowerCase();
            out = out.filter(e => e.firstname.toLowerCase().includes(search)
              || e.lastname.toLowerCase().includes(search));
          }
          return out;
        }
        return null;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      this.reload();
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      async reload() {
        this.users = null;
        const users = await this.$sav.api.get('/users');
        this.users = users.filter(u => u.uuid !== Store.uuid);
      },
      createUser() {
        const modal = VueUtil.createComponent(ModalUserCreateEdit, document.body, {
          propsData: {},
          parent: this,
        });
        modal.$once('done', async () => { this.reload(); });
      },
      async sendPassword(ref) {
        await this.$sav.api.post(`/user/${ref.uuid}/reset-pwd`);
        Toast.success('Neues Passwort wurde per SMS versandt.');
      },
      executeAction(action, reference) {
        switch (action) {
          case 'edit': {
            const modal = VueUtil.createComponent(ModalUserCreateEdit, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => { this.reload(); });
            break;
          }
          case 'lockToggle': {
            const modal = VueUtil.createComponent(ModalUserLock, document.body, {
              propsData: { uuid: reference.uuid, locked: reference.locked, inquiries: reference.inquiries },
              parent: this,
            });
            modal.$once('done', async () => { this.reload(); });
            break;
          }
          case 'delete': {
            const modal = VueUtil.createComponent(ModalUserDelete, document.body, {
              propsData: { uuid: reference.uuid, inquiries: reference.inquiries },
              parent: this,
            });
            modal.$once('done', async () => { this.reload(); });
            break;
          }
          case 'sendpw': {
            this.sendPassword(reference);
            break;
          }
          default: {
            console.log(action, reference);
            break;
          }
        }
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleListInteraction(data) {
        const { id, value } = data;
        const filters = Object.assign({}, this.filters);
        filters[id] = value;
        this.filters = filters;
      },
      handleItemSelect(data) {
        this.executeAction('edit', data);
      },
      handleListContext(evt) {
        const { currentTarget, row } = evt;
        const actions = this.computedListActions;
        const toggle = actions.find(a => a.action === 'lockToggle');
        toggle.label = (row.locked) ? 'Entsperren' : 'Sperren';
        const context = VueUtil.createComponent(ContextMenu, document.body, {
          propsData: {
            origin: currentTarget,
            reference: row,
            actions,
          },
          parent: this,
        });

        context.$once('action', (action, reference) => {
          context.destroy();
          this.executeAction(action, reference);
        });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.users {
}
</style>
